/**
 * Select Component V2
 *
 * WCAG 2.1 Level AA + BFSG konform
 * Built with Radix UI for accessibility
 */

import { forwardRef, useId, type ComponentPropsWithoutRef, type ElementRef } from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

// ============================================================================
// Types
// ============================================================================

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends ComponentPropsWithoutRef<typeof SelectPrimitive.Root> {
  /**
   * DOM id (forwarded to the trigger / label-htmlFor binding)
   */
  id?: string;

  /**
   * Select label (required for accessibility)
   */
  label?: string;

  /**
   * Options to display
   */
  options: SelectOption[];

  /**
   * Placeholder text
   */
  placeholder?: string;

  /**
   * Error message
   */
  error?: string;

  /**
   * Helper text
   */
  helperText?: string;

  /**
   * Required field
   */
  required?: boolean;
}

// ============================================================================
// Select Root
// ============================================================================

/**
 * Select Component
 *
 * Features:
 * - WCAG 2.1 AA compliant (keyboard navigation, ARIA labels)
 * - BFSG compliant (44x44px minimum touch target)
 * - Built on Radix UI for accessibility
 * - Dark/Light mode support
 * - Error and helper text support
 *
 * @example
 * ```tsx
 * <Select
 *   label="Channel"
 *   placeholder="Wähle einen Channel"
 *   options={[
 *     { value: 'amazon', label: 'Amazon' },
 *     { value: 'ebay', label: 'eBay' },
 *   ]}
 *   value={channel}
 *   onValueChange={setChannel}
 * />
 * ```
 */
export function Select({
  label,
  options,
  placeholder,
  error,
  helperText,
  required,
  disabled,
  ...props
}: SelectProps) {
  const { t } = useTranslation();
  const resolvedPlaceholder = placeholder ?? t('common.select');
  const autoId = useId();
  const selectId = autoId;
  const errorId = `${selectId}-error`;
  const helperId = `${selectId}-helper`;
  const hasError = Boolean(error);

  return (
    <div className="w-full">
      {/* Label */}
      {label && (
        <label
          htmlFor={selectId}
          className={cn(
            'block text-sm font-medium mb-1.5',
            'text-neutral-700 dark:text-neutral-300',
            disabled && 'opacity-50 cursor-not-allowed'
          )}
        >
          {label}
          {required && (
            <span className="text-red-600 dark:text-red-400 ml-1" aria-label={t('common.required')}>
              *
            </span>
          )}
        </label>
      )}

      {/* Select */}
      <SelectPrimitive.Root disabled={disabled} {...props}>
        <SelectPrimitive.Trigger
          id={selectId}
          className={cn(
            // Base styles
            'flex w-full items-center justify-between',
            'rounded-md border px-3 py-2',
            'bg-white dark:bg-neutral-900 text-sm',
            'text-neutral-900 dark:text-neutral-100',

            // BFSG: Touch targets minimum 44x44px (h-11 = 44px)
            'min-h-11',

            // Focus styles (WCAG 2.1: 3:1 contrast minimum)
            'focus-visible:outline-none',
            'focus-visible:ring-2 focus-visible:ring-offset-2',
            'focus-visible:ring-primary-500 dark:focus-visible:ring-offset-neutral-900',

            // Disabled state
            'disabled:cursor-not-allowed disabled:opacity-50',

            // Border color
            hasError
              ? 'border-red-600 dark:border-red-500 focus-visible:ring-red-600 dark:focus-visible:ring-red-500'
              : 'border-neutral-300 dark:border-neutral-700',

            // Placeholder
            'data-[placeholder]:text-neutral-400 dark:data-[placeholder]:text-neutral-500'
          )}
          aria-invalid={hasError}
          aria-describedby={cn(error && errorId, helperText && helperId)}
          aria-required={required}
        >
          <SelectPrimitive.Value placeholder={resolvedPlaceholder} />
          <SelectPrimitive.Icon asChild>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-neutral-500 dark:text-neutral-400"
            >
              <path d="m6 9 6 6 6-6" />
            </svg>
          </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>

        <SelectPrimitive.Portal>
          <SelectPrimitive.Content
            className={cn(
              'relative z-50 overflow-hidden rounded-md border',
              'bg-white dark:bg-neutral-900',
              'border-neutral-200 dark:border-neutral-800',
              'shadow-lg',
              // Animation
              'data-[state=open]:animate-in data-[state=closed]:animate-out',
              'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
              'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95'
            )}
            position="popper"
            sideOffset={4}
          >
            <SelectPrimitive.Viewport className="max-h-[var(--radix-select-content-available-height,300px)] overflow-y-auto p-1">
              {options.map((option) => (
                <SelectItem key={option.value} value={option.value} disabled={option.disabled}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectPrimitive.Viewport>
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>

      {/* Error Message */}
      {error && (
        <p
          id={errorId}
          className="mt-1.5 text-sm text-red-600 dark:text-red-400"
          role="alert"
        >
          {error}
        </p>
      )}

      {/* Helper Text */}
      {!error && helperText && (
        <p
          id={helperId}
          className="mt-1.5 text-sm text-neutral-500 dark:text-neutral-400"
        >
          {helperText}
        </p>
      )}
    </div>
  );
}

// ============================================================================
// Select Item
// ============================================================================

type SelectItemProps = ComponentPropsWithoutRef<typeof SelectPrimitive.Item>;

const SelectItem = forwardRef<ElementRef<typeof SelectPrimitive.Item>, SelectItemProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <SelectPrimitive.Item
        ref={ref}
        className={cn(
          'relative flex w-full cursor-pointer select-none items-center',
          'rounded-sm py-2 pl-8 pr-2',
          'text-sm outline-none',
          'text-neutral-900 dark:text-neutral-100',
          // Hover & Focus
          'focus:bg-primary-100 dark:focus:bg-primary-900/50',
          'data-[highlighted]:bg-primary-100 dark:data-[highlighted]:bg-primary-900/50',
          // Disabled
          'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
          // BFSG: Touch targets (h-11 = 44px)
          'min-h-11',
          className
        )}
        {...props}
      >
        <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
          <SelectPrimitive.ItemIndicator>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 6 9 17l-5-5" />
            </svg>
          </SelectPrimitive.ItemIndicator>
        </span>

        <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
      </SelectPrimitive.Item>
    );
  }
);

SelectItem.displayName = 'SelectItem';

Select.displayName = 'Select';

// ============================================================================
// Compositional exports for shadcn-style usage
// ============================================================================

const SelectTrigger = forwardRef<
  ElementRef<typeof SelectPrimitive.Trigger>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      'flex w-full items-center justify-between',
      'rounded-md border px-3 py-2',
      'bg-white dark:bg-neutral-900 text-sm',
      'text-neutral-900 dark:text-neutral-100',
      'min-h-11',
      'focus-visible:outline-none',
      'focus-visible:ring-2 focus-visible:ring-offset-2',
      'focus-visible:ring-primary-500 dark:focus-visible:ring-offset-neutral-900',
      'disabled:cursor-not-allowed disabled:opacity-50',
      'border-neutral-300 dark:border-neutral-700',
      'data-[placeholder]:text-neutral-400 dark:data-[placeholder]:text-neutral-500',
      className
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-neutral-500 dark:text-neutral-400"
      >
        <path d="m6 9 6 6 6-6" />
      </svg>
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = 'SelectTrigger';

const SelectContent = forwardRef<
  ElementRef<typeof SelectPrimitive.Content>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        'relative z-50 overflow-hidden rounded-md border',
        'bg-white dark:bg-neutral-900',
        'border-neutral-200 dark:border-neutral-800',
        'shadow-lg',
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        className
      )}
      position={position}
      sideOffset={4}
      {...props}
    >
      <SelectPrimitive.Viewport className="max-h-[var(--radix-select-content-available-height,300px)] overflow-y-auto p-1">{children}</SelectPrimitive.Viewport>
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = 'SelectContent';

const SelectValue = SelectPrimitive.Value;

// Re-export Root as SelectRoot for compositional usage
const SelectRoot = SelectPrimitive.Root;

export { SelectRoot, SelectTrigger, SelectContent, SelectValue, SelectItem };
